import { capitalize } from '../logger/utils/capitalize';
import { MfsStatusCode } from '../mfsStatusCodes';
import { defaultSendCondition } from '../telemetryProcessor/utils/telemetryUtilities';
import { BEGIN_LOGGING_TEXT, END_LOGGING_TEXT, isPromise } from './decoratorUtils';
/**
 * Gets a logActivity method decorator associated with the given `logger`.
 *
 * @param logger Logger to be used in decorator.
 * @returns LogActivity decorator.
 */
export function getLogActivityDecorator(logger) {
    /** Starts an activity for telemetry */
    function startActivity(activityName, activityEventType, operationName, logParams) {
        const message = `${BEGIN_LOGGING_TEXT}: ${activityName}`;
        logger.logVerbose(operationName, message);
        // prints out the exact same information, except now they're both the same case
        return logger.startActivity(capitalize(activityName), activityEventType, message, activityName, logParams);
    }
    /**
     * End activity with success for telemetry
     *
     * @param activity Activity that completed.
     * @param operationName Method name that the log is invoked from
     * @param shouldSend Send condition that must be met before the aggregated event is sent
     */
    function endActivitySuccess(activity, operationName, shouldSend) {
        logger.logVerbose(operationName, `${END_LOGGING_TEXT}: ${activity.activityName}`);
        logger.endActivitySuccess(activity, shouldSend);
    }
    /**
     * End activity with failure for telemetry
     *
     * @param activity Activity that completed.
     * @param operationName Method name that the log is invoked from
     * @param error Error for activity.
     */
    function endActivityFailure(activity, operationName, error) {
        logger.logVerbose(operationName, `${END_LOGGING_TEXT}: ${activity.activityName}`);
        logger.endActivityFailure(activity, error);
    }
    /**
     * Log a warning.
     *
     * @param eventName Name of the event for the activity.
     * @param operationName Name of the operation in the activity.
     * @param mfsStatusCode Status code result of the activity.
     * @param errorInfo Error information of the warning.
     * @param logParams Parameters to log to console.
     */
    function logWarn(eventName, operationName, mfsStatusCode, errorInfo, logParams) {
        logger.logWarn(`${capitalize(eventName)}`, mfsStatusCode, errorInfo, operationName, logParams);
    }
    return function (activityEventType, shouldSend, eventName) {
        /** A single decorator that works for both sync & async functions */
        return function logActivity(_target, methodName, descriptor) {
            const activityName = eventName || methodName;
            const originalMethod = descriptor.value;
            const predicate = shouldSend ? shouldSend : defaultSendCondition;
            descriptor.value = function (...args) {
                const activity = startActivity(activityName, activityEventType, methodName, { args });
                try {
                    const result = originalMethod.apply(this, args);
                    // check if function was async
                    if (isPromise(result)) {
                        // attach then and catch handlers to end the activity correctly
                        return result
                            .catch((error) => {
                            endActivityFailure(activity, methodName, error);
                            throw error;
                        })
                            .then((innerResult) => {
                            endActivitySuccess(activity, methodName, predicate);
                            return innerResult;
                        });
                    }
                    // sync function, just return the result and warn if failure
                    if (result && result.error) {
                        logWarn(activityName, methodName, MfsStatusCode.ExpectationFailed, {
                            message: 'Failure in log activity result',
                            error: result.error,
                        });
                    }
                    endActivitySuccess(activity, methodName, predicate);
                    return result;
                }
                catch (error) {
                    endActivityFailure(activity, methodName, error);
                    throw error;
                }
            };
            return descriptor;
        };
    };
}
//# sourceMappingURL=logActivity.js.map